import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { User } from '@/core/auth/types'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  timestamp: number
  link?: string  // Optional link to navigate to when clicking the notification
}

interface AppState {
  // User state
  user: User | null
  setUser: (user: User | null) => void
  
  // App preferences
  theme: 'light' | 'dark'
  language: 'fr' | 'en' | 'nl' | 'de'
  setLanguage: (language: 'fr' | 'en' | 'nl' | 'de') => void
  setPreference: <K extends keyof Preferences>(key: K, value: Preferences[K]) => void
  
  // Notifications
  notifications: Notification[]
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void
  removeNotification: (id: string) => void
  clearNotifications: () => void
  
  // UI state
  sidebarOpen: boolean
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
}

interface Preferences {
  theme: 'light' | 'dark'
  language: 'fr' | 'en' | 'nl' | 'de'
  notificationsEnabled: boolean
  soundEnabled: boolean
}

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        // User state
        user: null,
        setUser: (user) => set({ user }),
        
        // App preferences
        theme: 'light',
        language: 'fr',
        setLanguage: (language) => set({ language }),
        setPreference: (key, value) => set((state) => ({
          ...state,
          [key]: value
        })),
        
        // Notifications
        notifications: [],
        addNotification: (notification) => set((state) => {
          const newNotification = {
            ...notification,
            id: crypto.randomUUID(),
            timestamp: Date.now()
          };
          console.log('[AppStore] Adding notification:', newNotification);
          return {
            notifications: [
              ...state.notifications,
              newNotification
            ]
          };
        }),
        removeNotification: (id) => set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id)
        })),
        clearNotifications: () => set({ notifications: [] }),
        
        // UI state
        sidebarOpen: true,
        toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
        setSidebarOpen: (open) => set({ sidebarOpen: open })
      }),
      {
        name: 'app-storage',
        partialize: (state) => ({
          theme: state.theme,
          language: state.language,
          sidebarOpen: state.sidebarOpen
        })
      }
    )
  )
)